import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

import { buildPapDetail } from '../features/ttqib/papDisplay';
import { knowledgeService, type KnowledgeParticlePapView } from '../services/knowledgeService';
import { MathTextBlock } from './MathTextBlock';
import { SectionCard } from './ttq/SectionCard';

export type KnowledgeParticlePapFooterContext = {
  extract: (force?: boolean) => Promise<void>;
  extracting: boolean;
  running: boolean;
  eligible: boolean;
  hasContent: boolean;
  failed: boolean;
};

type KnowledgeParticlePapPanelProps = {
  particleId: string;
  /** 右下角操作区（MoreVertical）；可读取提取能力 */
  renderFooterActions?: (ctx: KnowledgeParticlePapFooterContext) => ReactNode;
  className?: string;
};

function extractionStatusLabel(status: KnowledgeParticlePapView['extractionStatus']): string {
  switch (status) {
    case 'pending': return '进行中';
    case 'running': return '正在提取隐性能力…';
    case 'failed': return '提取失败';
    case 'ready': return '提取完成';
    case 'partial': return '部分完成';
    default: return status;
  }
}

export function KnowledgeParticlePapPanel({
  particleId,
  renderFooterActions,
  className = '',
}: KnowledgeParticlePapPanelProps) {
  const [view, setView] = useState<KnowledgeParticlePapView | null>(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!particleId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      setView(await knowledgeService.getParticlePap(particleId, { kickoff: !silent }));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载隐性能力失败');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [particleId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const status = view?.extractionStatus;
    if (status !== 'running' && status !== 'pending') return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load(true);
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(true);
    }, 15_000);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [load, view?.extractionStatus]);

  const papDetail = useMemo(() => buildPapDetail(view?.pap ?? null), [view?.pap]);

  const extract = useCallback(async (force = false) => {
    if (!particleId || extracting) return;
    setExtracting(true);
    setError(null);
    try {
      setView(await knowledgeService.extractParticlePap(particleId, { force }));
      void load(true);
    } catch (extractError) {
      setError(extractError instanceof Error ? extractError.message : '提取隐性能力失败');
    } finally {
      setExtracting(false);
    }
  }, [extracting, load, particleId]);

  const running = view?.extractionStatus === 'running'
    || view?.extractionStatus === 'pending'
    || extracting;
  const failed = view?.extractionStatus === 'failed';
  const eligible = Boolean(view?.eligible);
  const footerCtx: KnowledgeParticlePapFooterContext = {
    extract,
    extracting,
    running,
    eligible,
    hasContent: papDetail.hasContent,
    failed: Boolean(failed),
  };
  const footer = renderFooterActions?.(footerCtx);

  if (loading && !view) {
    return (
      <SectionCard title="隐性能力" subtitle="正在加载…" className={className}>
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin text-primary" size={24} />
        </div>
        {footer ? <div className="flex justify-end pt-1">{footer}</div> : null}
      </SectionCard>
    );
  }

  const notEligible = view && !view.eligible;

  return (
    <SectionCard
      title="隐性能力"
      subtitle={view ? extractionStatusLabel(view.extractionStatus) : undefined}
      tone="accent"
      className={className}
    >
      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {notEligible ? (
        <p className="text-sm text-muted">当前颗粒暂不满足隐性能力提取条件。</p>
      ) : null}

      {!notEligible && running ? (
        <div className="flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary">
          <Loader2 className="animate-spin" size={16} />
          正在后台提取隐性能力包，请稍候…
        </div>
      ) : null}

      {!notEligible && failed ? (
        <div className="rounded-xl border border-danger/20 bg-danger/5 px-4 py-3">
          <p className="text-sm text-danger">{view?.failure?.message || '隐性能力提取失败'}</p>
          <p className="mt-1 text-xs text-muted">可从右下角菜单选择「重新提取隐性能力」。</p>
        </div>
      ) : null}

      {!notEligible && !running && !failed && !papDetail.hasContent ? (
        <p className="text-sm text-muted">尚未提取隐性能力，可从右下角菜单发起提取。</p>
      ) : null}

      {!notEligible && papDetail.hasContent && papDetail.implicitSteps.length === 0 && papDetail.matchedTags.length === 0 && papDetail.flowSummary ? (
        <p className="text-sm text-muted">隐性能力步骤与标签不完整，可从右下角菜单重新提取。</p>
      ) : null}

      {!notEligible && papDetail.hasContent ? (
        <div className="space-y-4">
          {papDetail.flowSummary ? (
            <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-primary">能力流概览</h3>
              <MathTextBlock text={papDetail.flowSummary} className="mt-2 text-sm leading-relaxed text-ink" />
            </div>
          ) : null}

          {papDetail.implicitSteps.length > 0 ? (
            <div className="rounded-xl border border-border bg-surface p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">隐性能力步骤</h3>
              <div className="mt-3 space-y-3">
                {papDetail.implicitSteps.map((step, index) => (
                  <div key={`${step.implicitAbility}-${index}`} className="flex gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {step.stepOrder ?? index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-1">
                        <MathTextBlock
                          text={step.implicitAbility}
                          inline
                          className="text-sm font-medium text-ink"
                        />
                        {step.implicitAbilityIsNew ? (
                          <span className="text-[10px] font-normal text-orange-500">新</span>
                        ) : null}
                      </div>
                      {step.actionObject ? (
                        <p className="mt-0.5 text-xs text-muted">
                          作用对象：
                          <MathTextBlock text={step.actionObject} inline className="text-xs text-muted" />
                        </p>
                      ) : null}
                      {step.reasoning ? (
                        <MathTextBlock text={step.reasoning} className="mt-1 text-xs leading-relaxed text-muted" />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {papDetail.matchedTags.length > 0 ? (
            <div className="rounded-xl border border-border bg-surface p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">匹配能力标签</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {papDetail.matchedTags.map((tag, index) => (
                  <span
                    key={`${tag.label}-${index}`}
                    className="rounded-full border border-primary/15 bg-primary/5 px-2.5 py-1 text-xs text-primary"
                    title={tag.reason}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {papDetail.examPointSteps.length > 0 ? (
            <div className="rounded-xl border border-border bg-surface p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">考点流</h3>
              <div className="mt-3 space-y-2">
                {papDetail.examPointSteps.map((step, index) => (
                  <div key={`${step.examPointLabel}-${index}`} className="flex gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black/[0.04] text-xs font-semibold text-muted">
                      {step.stepOrder ?? index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <MathTextBlock text={step.examPointLabel} className="text-sm text-ink" />
                      {step.reasoning ? (
                        <MathTextBlock text={step.reasoning} className="mt-0.5 text-xs text-muted" />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {footer ? <div className="flex justify-end pt-2">{footer}</div> : null}
    </SectionCard>
  );
}
